import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  addDays,
  addWeeks,
  addMonths,
  eachDayOfInterval,
  format,
  parseISO,
  isValid,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import type { CollectForTable } from "@/hooks/useCollectsApiData";

export type ColetasView = "agenda" | "semana" | "mes";

/** Quantos dias à frente a visão Agenda cobre a partir de hoje. */
export const AGENDA_RANGE_DAYS = 30;

/** Semana inicia na segunda (igual ao protótipo: SEG…DOM). */
const WEEK_OPTS = { weekStartsOn: 1 as const };

export interface DateWindow {
  start: Date;
  end: Date;
}

/** Janela de datas visível para cada visão, a partir de uma data de referência. */
export function getWindowForView(view: ColetasView, ref: Date): DateWindow {
  if (view === "semana") {
    return { start: startOfWeek(ref, WEEK_OPTS), end: endOfWeek(ref, WEEK_OPTS) };
  }
  if (view === "mes") {
    // Inclui os dias de "spillover" para preencher a grade (semanas completas).
    return {
      start: startOfWeek(startOfMonth(ref), WEEK_OPTS),
      end: endOfWeek(endOfMonth(ref), WEEK_OPTS),
    };
  }
  // Agenda: da data de referência até +N dias (permite navegar para frente/trás).
  return { start: ref, end: addDays(ref, AGENDA_RANGE_DAYS) };
}

/** Avança/retrocede a data de referência conforme a visão. */
export function stepRef(view: ColetasView, ref: Date, direction: 1 | -1): Date {
  if (view === "semana") return addWeeks(ref, direction);
  if (view === "mes") return addMonths(ref, direction);
  return addDays(ref, direction * AGENDA_RANGE_DAYS);
}

/**
 * Pode avançar? Bloqueia períodos fora do mês atual:
 * - Mês: sempre bloqueado (fixado no mês atual).
 * - Semana: bloqueado quando a próxima semana começaria em outro mês.
 */
export function canStepNext(view: ColetasView, ref: Date): boolean {
  if (view === "mes") return false;
  if (view === "semana") {
    const today = startOfToday();
    const nextWeekStart = startOfWeek(addWeeks(ref, 1), WEEK_OPTS);
    return (
      nextWeekStart.getMonth() === today.getMonth() &&
      nextWeekStart.getFullYear() === today.getFullYear()
    );
  }
  return true;
}

/**
 * Pode retroceder? Bloqueia períodos totalmente no passado — não deixa ver
 * semana/mês anteriores ao atual (nem agenda antes de hoje).
 */
export function canStepPrev(view: ColetasView, ref: Date): boolean {
  const today = startOfToday();
  if (view === "semana") {
    return (
      startOfWeek(ref, WEEK_OPTS).getTime() >
      startOfWeek(today, WEEK_OPTS).getTime()
    );
  }
  if (view === "mes") {
    return startOfMonth(ref).getTime() > startOfMonth(today).getTime();
  }
  return ref.getTime() > today.getTime();
}

/** Rótulo do período exibido ao lado da navegação. */
export function formatPeriodLabel(view: ColetasView, ref: Date): string {
  if (view === "semana") {
    const start = startOfWeek(ref, WEEK_OPTS);
    const end = endOfWeek(ref, WEEK_OPTS);
    const startLabel = format(start, "dd MMM", { locale: ptBR });
    const endLabel = format(end, "dd MMM yyyy", { locale: ptBR });
    return `${startLabel} – ${endLabel}`;
  }
  if (view === "mes") {
    return capitalize(format(ref, "MMMM 'de' yyyy", { locale: ptBR }));
  }
  const agendaEnd = addDays(ref, AGENDA_RANGE_DAYS);
  const startLabel = format(ref, "dd MMM", { locale: ptBR });
  const endLabel = format(agendaEnd, "dd MMM yyyy", { locale: ptBR });
  return `${startLabel} – ${endLabel}`;
}

/** Dias da semana (seg→dom) para a data de referência. */
export function getWeekDays(ref: Date): Date[] {
  const window = getWindowForView("semana", ref);
  return eachDayOfInterval({ start: window.start, end: window.end });
}

/** Semanas (linhas) da grade do mês, cada uma com 7 dias. */
export function getMonthWeeks(ref: Date): Date[][] {
  const window = getWindowForView("mes", ref);
  const days = eachDayOfInterval({ start: window.start, end: window.end });
  const weeks: Date[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }
  return weeks;
}

/** Chave de dia (YYYY-MM-DD) a partir de uma coleta. */
export function dayKey(date: Date): string {
  return format(date, "yyyy-MM-dd");
}

/** Agrupa coletas por dia (chave YYYY-MM-DD). */
export function groupCollectsByDay(
  collects: CollectForTable[],
): Map<string, CollectForTable[]> {
  const map = new Map<string, CollectForTable[]>();
  for (const collect of collects) {
    const parsed = parseCollectDate(collect.planned_date);
    if (!parsed) continue;
    const key = dayKey(parsed);
    const list = map.get(key);
    if (list) list.push(collect);
    else map.set(key, [collect]);
  }
  return map;
}

/** Converte o planned_date (string) em Date, tolerando formatos do Postgres. */
export function parseCollectDate(value?: string | null): Date | null {
  if (!value) return null;
  const normalized = value.length > 10 ? value : `${value}T00:00:00`;
  const parsed = parseISO(normalized);
  return isValid(parsed) ? parsed : null;
}

/** Rótulo de status para a UI de coletas (Próximas usa "Agendada" para planned). */
export function coletaStatusLabel(status?: string | null): string {
  switch ((status ?? "").toLowerCase()) {
    case "planned":
      return "Agendada";
    case "collected":
      return "Coletada";
    case "fault":
      return "Falha";
    default:
      return status || "—";
  }
}

/** Variant da Badge por status de coleta (Agendada = default). */
export function coletaStatusBadgeVariant(
  status?: string | null,
): "default" | "success" | "destructive" | "secondary" {
  switch ((status ?? "").toLowerCase()) {
    case "planned":
      return "default";
    case "collected":
      return "success";
    case "fault":
      return "destructive";
    default:
      return "secondary";
  }
}

/** Cidade/UF formatada para os cards (ex.: "São Paulo/SP"). */
export function formatCityState(
  city?: string | null,
  state?: string | null,
): string {
  if (city && state) return `${city}/${state}`;
  return city || state || "";
}

/**
 * Endereço completo do estabelecimento.
 * Linha 1: rua + número. Linha 2: bairro, cidade/UF.
 * Retorna array com as partes não-vazias.
 */
export function formatFullAddress(params: {
  street?: string | null;
  number?: string | null;
  neighborhood?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
}): string[] {
  const lines: string[] = [];

  const streetLine = [params.street, params.number].filter(Boolean).join(", ");
  if (streetLine) lines.push(streetLine);

  const secondLine = [params.neighborhood, formatCityState(params.city, params.state)]
    .filter(Boolean)
    .join(" — ");
  if (secondLine) lines.push(secondLine);

  return lines;
}

export function startOfToday(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

export function getCurrentWeekWindow(): DateWindow {
  return getWindowForView("semana", new Date());
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
